/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import styles from './RenderedHTML.module.scss';

/* Clone and make the image full screen */
function imgClickCallback(e: Event) {
  const img = e.target as HTMLImageElement;
  makeFullScreenImage(img);
}

/**
 * - Find all <img> tags and make the image clickable so that they can expand
 */
export function makeImagesExpandable(html: HTMLDivElement | Element | null) {
  html?.querySelectorAll('img').forEach((node) => {
    // Remove any existing event listeners
    // Remove any inline event handlers (if any exist)
    node.removeAttribute('onclick');
    node.addEventListener('click', imgClickCallback);
    if (node.complete) {
      checkIfExpandable(node); // Run immediately if already loaded
    } else {
      node.addEventListener('load', handleImageLoad); // Run when it loads
    }
  });
}

/**
 * - Find all <fhir> tags, and make them clickable, hoverable, and add additional information to them
 */
export function updateFhirTags(
  html: HTMLDivElement | Element | null,
  documentVersionId: string, // The is the document version that the <fhir> tag is in.  e.g. if the tag is in an embedded document, this is the embedded document version id, not the parent
  documentId: string | undefined, // Only used if embededed is true - this is the document id not the document version id
  pageId: string, // The page id that the <fhir> tag is in. e.g. if the tag is in an embedded document, this is the embedded document page id, not the parent
  pageName: string | undefined, // Only used if embededed is true - must be same page as for pageId
  isEmbedded: boolean
) {
  html?.querySelectorAll('fhir').forEach((node) => {
    const embeddedParent = node.closest('embeddocumentpage');
    if (!embeddedParent && isEmbedded) {
      // Should be impossible
      throw new Error('Internal Error: Embedded parent not found');
    } else if (embeddedParent) {
      if (!isEmbedded)
        // If the <fhir> tag is in an embedded document, ignore it.  E.g. an embedded document inside a document but we only want to update the document
        return;
      // embeddedParent should match html
      if (embeddedParent !== html) {
        return;
      }

      // Following checks shouldn't be necessary and shouldn't fail because embeddedParent === html
      // Make sure the documentId matches the data-doc-id attribute
      if (embeddedParent.getAttribute('data-doc-id') !== documentId)
        // skip it - this is when we have an embedded document inside another embedded document
        throw new Error('Internal Error: Embedded document id does not match');
      // Check page name matches
      if (embeddedParent.getAttribute('data-page') !== pageName)
        // skip it - wrong page
        throw new Error('Internal Error: Embedded page name does not match');
    }
    node.addEventListener('mouseenter', (e) => {
      window.fhirHighlight(e, true, false /* not in CK editor */, documentVersionId, pageId);
    });
    node.addEventListener('mouseleave', (e) => {
      window.fhirHighlight(e, false, false /* not in CK editor */);
    });
    node.addEventListener('click', window.fhirClick);
    if (isEmbedded) {
      if (!documentId) throw new Error('Document id is required when isEmbedded is true');
      // When we have embedded fhir tags, we need to also add embedded doc id and page id
      node.setAttribute('data-doc-id', documentId);
      node.setAttribute('data-page-id', pageId);
    }
  });
}

/* Returns true if there was a full screen image to remove */
export function closeFullScreenImage(): boolean {
  document.body.classList.remove(styles.bodyNoScroll!);
  const existingExpanded = document.querySelectorAll('.' + styles.fullscreenImg!);
  if (!existingExpanded.length) return false;
  existingExpanded.forEach((node) => node.remove());
  document.removeEventListener('keydown', handleEscape);
  return true;
}

function handleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    closeFullScreenImage();
  }
}

const FULLSCREEN_MIN_WIDTH = 150;
const FULLSCREEN_MIN_HEIGHT = 150;

/** Prevent fullscreen for small images (e.g., icons, emojis) */
function checkIfExpandable(img: HTMLImageElement) {
  // Ensure the image is loaded and meets size criteria
  if (img.complete && img.naturalWidth >= FULLSCREEN_MIN_WIDTH && img.naturalHeight >= FULLSCREEN_MIN_HEIGHT) {
    img.classList.add(styles.expandable!); // Add class to indicate expandability
  } else {
    img.classList.remove(styles.expandable!); // Remove if it doesn't meet criteria
  }
}

// Run check when image loads
function handleImageLoad(event: Event) {
  const img = event.target as HTMLImageElement;
  checkIfExpandable(img);
}

function makeFullScreenImage(img: HTMLImageElement) {
  if (closeFullScreenImage()) return;

  // Ensure the image is loaded before fullscreen
  if (!img.complete || img.naturalWidth === 0) return;

  // Prevent fullscreen for small images (e.g., icons, emojis)
  // Only fullscreen if the image is expandable
  if (!img.classList.contains(styles.expandable!)) return;

  const fullScreenImg = img.cloneNode() as HTMLImageElement;
  fullScreenImg.classList.add(styles.fullscreenImg!);
  fullScreenImg.setAttribute('tabindex', '-1');
  fullScreenImg.setAttribute('role', 'dialog'); // For screen readers

  fullScreenImg.addEventListener('click', closeFullScreenImage);

  document.body.appendChild(fullScreenImg);
  document.body.classList.add(styles.bodyNoScroll!);
  document.addEventListener('keydown', handleEscape);
  // Trigger fade-in transition by adding class after next frame
  requestAnimationFrame(() => {
    fullScreenImg.classList.add(styles.visible!);
  });
}
